import { Component, computed, effect, ElementRef, inject, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { SidebarShortcutsComponent } from './shortcuts/sidebar-shortcuts.component';
import { SidebarItemComponent } from './sidebar-item/sidebar-item.component';
import { SidebarService } from './sidebar.service';

@Component({
    selector: 'app-sidebar',
    host: {
        '(document:click)': 'onDocumentClick($event)',
        '(document:keydown)': 'onDocumentKeyDown($event)'
    },
    imports: [InputTextModule, SidebarItemComponent, SidebarShortcutsComponent],
    templateUrl: './sidebar.component.html'
})
export class SidebarComponent {
    private sidebarService = inject(SidebarService);
    private router = inject(Router);
    private sidebarElement = viewChild<ElementRef<HTMLDivElement>>('sidebarElement');
    private sidebarSearchInput = viewChild<ElementRef<HTMLInputElement>>('sidebarSearchInput');

    isSidebarOpen = this.sidebarService.isSidebarOpen;
    filteredSidebarItems = this.sidebarService.sidebarItems;
    homeSidebarItems = computed(() => this.filteredSidebarItems().filter((item) => item.section === 'home'));
    domainSidebarItems = computed(() => this.filteredSidebarItems().filter((item) => !item.section));
    adminSidebarItems = computed(() => this.filteredSidebarItems().filter((item) => item.section === 'system'));
    /* Non-empty sections in render order; the template puts a hairline divider above each. */
    visibleSections = computed(() => [this.homeSidebarItems(), this.domainSidebarItems(), this.adminSidebarItems()].filter((items) => items.length > 0));
    sidebarSearchTerm = this.sidebarService.sidebarSearchTerm;
    isSearching = this.sidebarService.isSearching;

    constructor() {
        effect(() => {
            if (this.isSidebarOpen()) {
                // Wait one tick so the input is ready after the sidebar opens.
                setTimeout(() => this.sidebarSearchInput()?.nativeElement.focus(), 0);
            }
        });
    }

    onSearchTermInput(event: Event) {
        this.sidebarService.updateSidebarSearchTerm((event.target as HTMLInputElement).value);
        this.scrollActiveSearchResultIntoView();
    }

    onSearchCleared() {
        this.sidebarService.clearSidebarSearchTerm();
        this.sidebarSearchInput()?.nativeElement.focus();
    }

    onSearchInputKeyDown(event: KeyboardEvent) {
        if (event.key === 'ArrowDown') {
            // Keep focus in search while moving the active menu result.
            event.preventDefault();
            this.sidebarService.selectNextSearchResult();
            this.scrollActiveSearchResultIntoView();
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.sidebarService.selectPreviousSearchResult();
            this.scrollActiveSearchResultIntoView();
        } else if (event.key === 'Enter') {
            this.openActiveSearchResult(event);
        }
    }

    onDocumentClick(event: PointerEvent) {
        if (!this.isSidebarOpen()) {
            return;
        }

        if (this.sidebarElement()?.nativeElement.contains(event.target as Node)) {
            return;
        }

        this.sidebarService.closeSidebar();
    }

    onDocumentKeyDown(event: KeyboardEvent) {
        if (!this.isSidebarOpen() || event.code !== 'Escape') {
            return;
        }

        // Two-step Escape: first press clears an active search, second press closes.
        if (this.isSearching()) {
            this.sidebarService.clearSidebarSearchTerm();
            this.sidebarSearchInput()?.nativeElement.focus();
            return;
        }

        this.sidebarService.closeSidebar();
    }

    private scrollActiveSearchResultIntoView() {
        // Wait for Angular to render the new active-result attribute before scrolling.
        setTimeout(() => document.querySelector('[data-active-search-result="true"]')?.scrollIntoView?.({ block: 'nearest' }), 0);
    }

    private openActiveSearchResult(event: KeyboardEvent) {
        const activeSearchResult = this.sidebarService.activeSearchResult();

        if (!activeSearchResult) {
            return;
        }

        event.preventDefault();
        this.sidebarService.closeSidebar();

        if (activeSearchResult.routerLink) {
            void this.router.navigateByUrl(activeSearchResult.routerLink);
            return;
        }

        if (activeSearchResult.url) {
            const resolvedUrl = this.sidebarService.resolveExternalUrl(activeSearchResult);

            if (resolvedUrl) {
                window.location.assign(resolvedUrl);
            }
        }
    }
}
